import type { ComponentType, ReactNode } from 'react';
import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

type DataStatePanelMode = 'loading' | 'empty';
type DataStatePanelSurface = 'card' | 'ghost';

interface DataStatePanelProps {
  title: string;
  description: string;
  icon?: ComponentType<{ className?: string }>;
  mode?: DataStatePanelMode;
  action?: ReactNode;
  className?: string;
  contentClassName?: string;
  iconClassName?: string;
  surface?: DataStatePanelSurface;
  'data-testid'?: string;
}

export function DataStatePanel({
  title,
  description,
  icon: Icon,
  mode = 'empty',
  action,
  className,
  contentClassName,
  iconClassName,
  surface = 'card',
  'data-testid': dataTestId,
}: DataStatePanelProps) {
  const StatusIcon = mode === 'loading' ? Loader2 : Icon;

  return (
    <div
      className={cn(
        'flex items-center justify-center px-6 py-10 text-center',
        surface === 'card' && 'rounded-2xl border border-border/60 bg-card/95 shadow-sm',
        className,
      )}
      data-testid={dataTestId}
    >
      <div className={cn('mx-auto flex w-full max-w-xl flex-col items-center', contentClassName)}>
        {StatusIcon ? (
          <div
            className={cn(
              'mb-5 flex h-16 w-16 items-center justify-center rounded-2xl bg-muted text-muted-foreground',
              mode === 'loading' && 'bg-primary/10 text-primary',
              iconClassName,
            )}
          >
            <StatusIcon className={cn('h-8 w-8', mode === 'loading' && 'animate-spin')} />
          </div>
        ) : null}

        <h3 className="font-heading text-xl font-semibold text-foreground">{title}</h3>
        <p className="mt-2 max-w-lg text-sm leading-6 text-muted-foreground">{description}</p>
        {action ? <div className="mt-5">{action}</div> : null}
      </div>
    </div>
  );
}
